/**
 * @file `/courses/:courseId` — course landing as a path of Parts.
 * @module @labcraft/lab/ui/pages/course_landing
 *
 * Umbrella #927 / part #940. Fetches the per-course summary via
 * `app.courses.get(courseId)` (404 → `/catalog`) and renders
 * its Parts as a vertical path with `done · now · next · locked`
 * state, derived pointer-only from the learner's `currentChapter`
 * against the `Part.prereqs` DAG ({@link computePartsPath}). An
 * unlocked Part navigates to its first chapter (or, for the current
 * Part, straight back to where the learner left off).
 */

import { useEffect, useState, type JSX } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useClient } from '@labcraft/core/frontend/react'
import { track } from '@labcraft/ui/hooks'
import type { Lab } from '../../client/index.ts'
import type { CourseSummary } from '../../client/courses.ts'
import type { PartListing } from '../../shared/routes.ts'
import { useMe, usePassedPartIds } from '../hooks/index.ts'
import { computePartsPath, type PartPathState } from '../lib/parts_path.ts'
import styles from './course_landing.module.css'

/** Marker glyph + word per state — color is never the sole signal. */
const STATE_PRESENTATION: Record<
  PartPathState,
  { marker: string; word: string; className: string | undefined }
> = {
  done: { marker: '✓', word: 'Done', className: styles.done },
  now: { marker: '●', word: 'Now', className: styles.now },
  next: { marker: '○', word: 'Next', className: styles.next },
  locked: { marker: '–', word: 'Locked', className: styles.locked },
}

export function CourseLandingPage(): JSX.Element | null {
  const app = useClient<Lab>()
  const navigate = useNavigate()
  const params = useParams()
  const courseId = params.courseId ?? ''
  const me = useMe()
  const [summary, setSummary] = useState<CourseSummary | null>(null)
  // Umbrella #1446 Part B — the learner's passed-Part set for this course,
  // from the auth-scoped progress surface. Drives `done` on the path so a
  // passed (single-)Part reads Done instead of looping on `now`. A fetch
  // failure surfaces as `progress.isError` (retryable) rather than a silent
  // `[]` that would downgrade a passed Part back to incomplete.
  const progress = usePassedPartIds(courseId)

  useEffect(() => {
    let active = true
    app.courses
      .get(courseId)
      .then(found => {
        if (!active) return
        if (!found) {
          navigate('/catalog', { replace: true })
          return
        }
        setSummary(found)
      })
      .catch((err: unknown) => {
        console.error('Failed to load course', err)
        if (active) navigate('/catalog', { replace: true })
      })
    return () => {
      active = false
    }
  }, [app, courseId, navigate])

  if (!summary) return null

  // The current Part is the learner's chapter pointer when it points into
  // THIS course; otherwise null (fresh start).
  const currentChapter = me.data?.learner?.currentChapter ?? null
  const currentPartId =
    currentChapter && currentChapter.courseId === courseId
      ? currentChapter.partId
      : null
  const passedPartIds = progress.data ?? []
  const path = computePartsPath(summary.parts, currentPartId, passedPartIds)

  /** Where an unlocked Part sends the learner. `null` = nowhere to go. */
  function hrefFor(part: PartListing, state: PartPathState): string | null {
    // The current Part resumes exactly where the learner left off.
    if (state === 'now' && currentChapter) {
      return `/courses/${courseId}/${currentChapter.partId}/${currentChapter.chapterId}`
    }
    const first = part.chapters[0]
    return first ? `/courses/${courseId}/${part.id}/${first.id}` : null
  }

  function openPart(part: PartListing, state: PartPathState): void {
    if (state === 'locked') return
    const href = hrefFor(part, state)
    if (!href) return
    track('course_part_open', { courseId, partId: part.id, state })
    navigate(href)
  }

  return (
    <main className={styles.main}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <button
          type="button"
          className={styles.backLink}
          onClick={() => {
            track('course_back_to_catalog', { courseId })
            navigate('/catalog')
          }}>
          <span aria-hidden>←</span> All courses
        </button>
      </nav>
      <p className={styles.eyebrow}>{summary.course.title}</p>
      <h1 className={styles.title}>{summary.course.tagline}</h1>
      <p className={styles.tagline}>
        Parts unlock as you clear what comes before them. Pick up where you left
        off, or jump to anything that’s open.
      </p>

      {progress.isError ? (
        <div className={styles.progressError} role="alert">
          <span>
            Couldn’t load your progress — some Parts may show as incomplete.
          </span>
          <button
            type="button"
            className={styles.retry}
            onClick={() => void progress.refetch()}>
            Retry
          </button>
        </div>
      ) : null}

      <ol className={styles.path}>
        {path.map(({ part, state }) => {
          const ui = STATE_PRESENTATION[state]
          const locked = state === 'locked'
          return (
            <li key={part.id}>
              <button
                type="button"
                className={`${styles.row} ${ui.className}`}
                disabled={locked}
                aria-disabled={locked}
                onClick={() => openPart(part, state)}>
                <span className={styles.marker} aria-hidden>
                  {ui.marker}
                </span>
                <span className={styles.body}>
                  <span className={styles.partTitle}>{part.title}</span>
                  <span className={styles.meta}>
                    {part.chapters.length} chapter
                    {part.chapters.length === 1 ? '' : 's'}
                    {part.tier === 'paid' ? ' · paid' : ' · free'}
                  </span>
                </span>
                <span className={styles.word}>{ui.word}</span>
              </button>
            </li>
          )
        })}

        {/* Planned-but-unbuilt Parts (own-course `designed` graph nodes).
            Name only + an `Upcoming` word — no chapter count / tier, since
            the Part doesn't exist yet — and never navigable. */}
        {summary.upcomingParts.map(part => (
          <li key={part.id}>
            <button
              type="button"
              className={`${styles.row} ${styles.upcoming}`}
              disabled
              aria-disabled={true}>
              <span className={styles.marker} aria-hidden>
                ·
              </span>
              <span className={styles.body}>
                <span className={styles.partTitle}>{part.title}</span>
              </span>
              <span className={styles.word}>Upcoming</span>
            </button>
          </li>
        ))}
      </ol>
    </main>
  )
}
